import { useState } from 'react';
import type { ReactNode } from 'react';
import { Contract, Partnership } from '@/types/entities';
import { EntityDrawer } from '@/components/ui/EntityDrawer';
import { useI18n } from '@/i18n';

export interface ContractFormValues {
  partnershipId: string;
  title: string;
  contractType: string;
  version: string;
  status: string;
  effectiveDate: string;
  expiryDate: string;
  currency: string;
  contractValue: number;
  notes: string;
}

const STATUSES = ['draft', 'under_review', 'waiting_partner', 'waiting_ugarits', 'ready', 'partially_signed', 'fully_signed', 'active', 'expiring_soon', 'expired', 'terminated'];

function toValues(c?: Contract | null, defaultPartnershipId?: string): ContractFormValues {
  return {
    partnershipId: c?.partnershipId ?? defaultPartnershipId ?? '',
    title: c?.title ?? '',
    contractType: c?.contractType ?? '',
    version: c?.version ?? 'v1',
    status: c?.status ?? 'draft',
    effectiveDate: c?.effectiveDate ?? '',
    expiryDate: c?.expiryDate ?? '',
    currency: c?.currency ?? 'AED',
    contractValue: c?.contractValue ?? 0,
    notes: c?.notes ?? '',
  };
}

export function ContractFormDrawer({
  contract, partnerships, defaultPartnershipId, onClose, onSubmit,
}: {
  contract?: Contract | null;
  partnerships: Partnership[];
  defaultPartnershipId?: string;
  onClose: () => void;
  onSubmit: (v: ContractFormValues) => void;
}) {
  const { t } = useI18n();
  const [values, setValues] = useState<ContractFormValues>(toValues(contract, defaultPartnershipId));
  const [error, setError] = useState('');

  function set<K extends keyof ContractFormValues>(key: K, val: ContractFormValues[K]) {
    setValues((v) => ({ ...v, [key]: val }));
  }

  function submit() {
    if (!values.partnershipId) { setError(t('contractForm.partnershipRequired')); return; }
    if (!values.title.trim()) { setError(t('contractForm.titleRequired')); return; }
    onSubmit(values);
  }

  return (
    <EntityDrawer title={contract ? t('contractForm.editTitle') : t('contractForm.newContract')} onClose={onClose}>
      <Field label={t('contractForm.partnership')} required>
        <select className="text-input" value={values.partnershipId} onChange={(e) => set('partnershipId', e.target.value)}>
          <option value="">—</option>
          {partnerships.map((p) => <option key={p.id} value={p.id}>{p.id} — {p.partnerName}</option>)}
        </select>
      </Field>
      <Field label={t('contractForm.title')} required>
        <input className="text-input" value={values.title} onChange={(e) => set('title', e.target.value)} />
      </Field>
      <Field label={t('contractForm.type')}><input className="text-input" value={values.contractType} onChange={(e) => set('contractType', e.target.value)} /></Field>
      <Field label={t('contractForm.version')}><input className="text-input" value={values.version} onChange={(e) => set('version', e.target.value)} /></Field>
      <Field label="Status">
        <select className="text-input" value={values.status} onChange={(e) => set('status', e.target.value)}>
          {STATUSES.map((s) => <option key={s} value={s}>{t(`status.contract.${s.replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`)}</option>)}
        </select>
      </Field>
      <Field label={t('contractForm.effectiveDate')}><input type="date" className="text-input" value={values.effectiveDate} onChange={(e) => set('effectiveDate', e.target.value)} /></Field>
      <Field label={t('contractForm.expiryDate')}><input type="date" className="text-input" value={values.expiryDate} onChange={(e) => set('expiryDate', e.target.value)} /></Field>
      <Field label={t('contractForm.currency')}><input className="text-input" value={values.currency} onChange={(e) => set('currency', e.target.value)} /></Field>
      <Field label={t('contractForm.value')}><input type="number" className="text-input" value={values.contractValue} onChange={(e) => set('contractValue', Number(e.target.value))} /></Field>
      <Field label="Notes"><textarea className="text-input" rows={3} value={values.notes} onChange={(e) => set('notes', e.target.value)} /></Field>

      {error && <div style={{ fontSize: 12, color: 'var(--crit)', marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn-primary" onClick={submit}>{t('common.save')}</button>
        <button onClick={onClose}>{t('common.cancel')}</button>
      </div>
    </EntityDrawer>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>{label}{required && <span style={{ color: 'var(--crit)' }}> *</span>}</label>
      {children}
    </div>
  );
}
